import { Box } from 'components/box';
import { Heading } from 'components/typography';
import Color from '../../../../util/color';
import { useCallback, useEffect, useRef, useState } from 'react';
import RichText from 'components/rich-text';
import { css } from '@emotion/react';
import { zIndices } from 'constants/z-indices';

const ENTER_KEY_CODE = 13;
const DEFAULT_HEIGHT = 350;

const ProductMarkup = ({
  body,
  color,
  title,
  highlighted,
}: {
  body: string;
  color?: string;
  title?: string;
  highlighted?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string | number>(
    highlighted ? 'auto' : DEFAULT_HEIGHT
  );
  const [expanded, setExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(!highlighted);

  useEffect(() => {
    if (ref.current && ref.current.scrollHeight) {
      const leeway = 15;
      const fullHeight = ref.current.scrollHeight;
      if (fullHeight <= DEFAULT_HEIGHT + (fullHeight / 100) * leeway) {
        setShowReadMore(false);
      }
    }
  }, [ref]);

  const toggle = useCallback(() => {
    if (ref.current?.scrollHeight) {
      setHeight(expanded ? DEFAULT_HEIGHT : ref.current.scrollHeight);
      setExpanded(!expanded);
    }
  }, [expanded, ref]);

  const onReadMoreKeyUp = e =>
    (e.which || e.keyCode) === ENTER_KEY_CODE && toggle();

  if (!body) {
    return null;
  }

  const padding = [highlighted ? 3 : null, null, highlighted ? 4 : null];

  return (
    <Box
      pt={padding}
      px={padding}
      css={theme => css`
        position: relative;
        word-break: break-word;
        -webkit-hyphens: auto;
        -moz-hyphens: auto;
        -ms-hyphens: auto;
        hyphens: auto;
        ${highlighted &&
        color &&
        css`
          border-radius: 10px;
          background: ${Color(color).alpha(0.2).rgb().string()};
        `}

        ${highlighted &&
        !color &&
        css`
          border: 1px solid ${theme.colors.lightGrey};
          background: ${theme.colors.lightestGrey};
          border-radius: 10px;
        `}
      `}
    >
      {!!title && (
        <Heading mb="1em" fontSize={5} fontWeight={700}>
          {title}
        </Heading>
      )}
      <Box
        css={css`
          transition: all 0.4s ease;
          overflow: hidden;
          overflow-anchor: none;
        `}
        style={{ height }}
      >
        <Box
          ref={ref}
          pb={showReadMore ? 5 : padding}
          css={theme => css`
            overflow: hidden;
            overflow-anchor: none;

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
              font-weight: 700 !important;
              font-family: ${theme.fonts.header} !important;
              line-height: 1.2 !important;
              color: ${highlighted
                ? color || theme.colors.pastelText
                : theme.colors.black} !important;

              width: 100% !important;
              line-height: 1.1;
              padding-bottom: 0.8em;
            }

            h1 {
              font-size: ${theme.fontSizes[5]};
            }

            h2 {
              font-size: ${theme.fontSizes[4]};
            }

            h3,
            h4,
            h5 {
              font-size: ${theme.fontSizes[3]};
            }

            p,
            li {
              line-height: 1.6em !important;
              width: 100% !important;
            }

            p {
              margin-bottom: 0.8em !important;
            }

            p:last-child {
              margin-bottom: 0 !important;
            }

            a {
              color: ${theme.colors.primary};
              text-decoration: none;
            }

            ul,
            ol {
              margin: 0 0 1em;
              padding-left: 2em;
              width: 100% !important;
            }

            ul {
              list-style-type: disc;
            }

            ol {
              list-style-type: decimal;
            }

            img {
              max-width: 100%;
            }

            @media ${theme.mediaQueries.mobileDown} {
              p {
                line-height: 1.4rem !important;
                margin-bottom: 0.5em !important;
                font-size: 0.9rem !important;
              }
            }
          `}
        >
          <RichText content={body} />
        </Box>
      </Box>

      {showReadMore && (
        <Box
          css={theme => css`
            height: 60px;
            width: 100%;
            overflow-anchor: none;
            background: linear-gradient(
              to top,
              rgba(255, 255, 255, 1) 0%,
              rgba(255, 255, 255, 1) 40%,
              rgba(255, 255, 255, 0.7) 65%,
              rgba(255, 255, 255, 0) 100%
            );
            overflow-anchor: none;
            position: absolute;
            bottom: 0;
            left: 0;
            cursor: pointer;
            z-index: ${zIndices.PRODUCT_READ_MORE};
            display: flex;

            span {
              font-family: ${theme.fonts.header};
              font-size: 0.9rem;
              font-weight: 700;
              color: ${theme.colors.link};
              text-transform: uppercase;
              align-self: flex-end;
            }

            &:focus {
              outline: none;
              border: none;
            }
          `}
          onClick={toggle}
          onKeyUp={onReadMoreKeyUp}
          role="button"
          tabIndex={0}
        >
          <span>{expanded ? 'read less' : 'read more'}</span>
        </Box>
      )}
    </Box>
  );
};

export default ProductMarkup;
